import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { materialsStyles } from "../../styles/materials/materialsTableStyles";
import {
  MATERIALS_ENDPOINT,
  MATERIALS_MAX_RETRIES,
  MATERIALS_REFRESH_INTERVAL,
  MATERIALS_RETRY_DELAY,
} from "../../utils/consts/materialsConsts";
import { isTokenValid, normalizeMaterials } from "../../utils/materialsUtils";

import { MaterialsTable } from "./MaterialsTable";

const UNAUTHORIZED = "materialsUnauthorized";
const LOAD_ERROR = "materialsLoadError";

export const MaterialsPanel = ({ accessToken }) => {
  const { t } = useTranslation();
  const [materials, setMaterials] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const retryTimerRef = useRef(null);

  useEffect(() => {
    let isCancelled = false;
    const clearRetry = () => {
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };

    const failWith = (key) => {
      setMaterials(null);
      setErrorKey(key);
      setIsLoading(false);
    };

    const loadMaterials = async (attempt = 0) => {
      // No usable token means the request never leaves the browser.
      if (!isTokenValid(accessToken)) {
        failWith(UNAUTHORIZED);
        return;
      }

      try {
        const response = await fetch(MATERIALS_ENDPOINT, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        if (isCancelled) return;

        // A rejected token is final - retrying it would only loop.
        if (response.status === 401 || response.status === 403) {
          failWith(UNAUTHORIZED);
          return;
        }
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) return;

        setMaterials(normalizeMaterials(data));
        setErrorKey(null);
        setIsLoading(false);
      } catch (error) {
        if (isCancelled) return;

        if (attempt < MATERIALS_MAX_RETRIES) {
          retryTimerRef.current = setTimeout(
            () => loadMaterials(attempt + 1),
            MATERIALS_RETRY_DELAY
          );
          return;
        }
        failWith(LOAD_ERROR);
      }
    };

    loadMaterials();
    const intervalId = setInterval(() => loadMaterials(), MATERIALS_REFRESH_INTERVAL);

    return () => {
      isCancelled = true;
      clearRetry();
      clearInterval(intervalId);
    };
  }, [accessToken]);

  if (isLoading) {
    return (
      <Box sx={materialsStyles.statusBox}>
        <CircularProgress color="error" />
        <Typography>{t("loading")}</Typography>
      </Box>
    );
  }

  if (errorKey) {
    return (
      <Box sx={materialsStyles.statusBox}>
        <Typography>{t(errorKey)}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={materialsStyles.container}>
      <MaterialsTable materials={materials} />
    </Box>
  );
};
